import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin, Plus, Target } from 'lucide-react';
import { SafeImage } from '@/components/ui/safe-image';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { StoreProfileDetail } from '../types';
import { createZoneFromInstoreLocation, sanitizeFloorplanWritePayload } from '../lib/floorplanZoneUtils';
import {
  getInstallImagesForInstoreLocation,
  groupCampaignsByInstoreLocation,
  locationsMissingFloorplanBox,
} from '../lib/instoreLocationGroups';
import { resolveEvidenceImageUrl } from '../lib/evidenceImageUrl';
import { ComplianceBadge } from './ComplianceBadge';
import { ProvenanceBadge } from './ProvenanceBadge';

interface TwinLocationCampaignsPanelProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
  selectedZoneId?: string | null;
  selectedInstoreLocationId?: number | null;
  zoneOutlineColors: Record<string, string>;
  onSelectZone?: (zoneId: string | null) => void;
  onSelectInstoreLocation?: (instoreLocationId: number | null) => void;
}

async function invalidateFloorplanQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  storeIdParam: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
    queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
  ]);
}

/**
 * Right-hand twin panel: legend of floorplan zone outline colours plus campaigns
 * grouped by posting-instruction in-store location. Locations without a drawn
 * box can be added to the floorplan in one action.
 */
export function TwinLocationCampaignsPanel({
  profile,
  storeId,
  storeIdParam,
  selectedZoneId,
  selectedInstoreLocationId,
  zoneOutlineColors,
  onSelectZone,
  onSelectInstoreLocation,
}: TwinLocationCampaignsPanelProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [addingLocationId, setAddingLocationId] = useState<number | null>(null);

  const locationNameById = useMemo(
    () => new Map((profile.retailerInstoreLocations ?? []).map((loc) => [loc.id, loc.name])),
    [profile.retailerInstoreLocations],
  );

  const campaignGroups = useMemo(
    () => groupCampaignsByInstoreLocation(profile.campaigns, profile.floorplanZones, locationNameById),
    [profile.campaigns, profile.floorplanZones, locationNameById],
  );

  const missingBoxes = useMemo(
    () => locationsMissingFloorplanBox(campaignGroups),
    [campaignGroups],
  );

  const legendZones = useMemo(() => {
    return profile.floorplanZones.map((zone) => ({
      zone,
      color: zoneOutlineColors[zone.id] ?? '#64748b',
      campaignCount: campaignGroups.find(
        (group) => group.floorplanZoneId === zone.id,
      )?.campaigns.length ?? 0,
    }));
  }, [profile.floorplanZones, zoneOutlineColors, campaignGroups]);

  const handleAddZone = async (instoreLocationId: number, locationName: string) => {
    if (!storeId || !storeIdParam) return;
    setAddingLocationId(instoreLocationId);
    try {
      const location = { id: instoreLocationId, name: locationName };
      const newZone = createZoneFromInstoreLocation(
        location,
        profile.floorplanZones.length,
        profile.floorplanZones,
      );
      const payload = sanitizeFloorplanWritePayload({
        aisles: profile.aisles.map(({ dataSource: _dataSource, ...aisle }) => aisle) as unknown as Array<Record<string, unknown>>,
        zones: [...profile.floorplanZones, newZone] as unknown as Array<Record<string, unknown>>,
      });
      const res = await apiRequest(`/api/store-profiles/${storeId}/floorplan`, 'PUT', payload);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = Array.isArray(body.details)
          ? body.details.map((item: { message?: string }) => item.message).filter(Boolean).join('; ')
          : null;
        throw new Error(detail || body.error || 'Failed to add location to floorplan');
      }
      await invalidateFloorplanQueries(queryClient, storeIdParam);
      onSelectZone?.(newZone.id);
      onSelectInstoreLocation?.(instoreLocationId);
      toast({
        title: 'Location added to floorplan',
        description: `${locationName} is now on the plan — drag it into position in floorplan setup if needed.`,
      });
    } catch (error) {
      toast({
        title: 'Could not add location',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setAddingLocationId(null);
    }
  };

  const selectGroup = (group: { instoreLocationId: number; floorplanZoneId: string | null }) => {
    onSelectInstoreLocation?.(group.instoreLocationId);
    onSelectZone?.(group.floorplanZoneId);
  };

  return (
    <Card className="min-w-0 xl:max-h-[720px] xl:overflow-y-auto">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5">
            <Target className="h-4 w-4" aria-hidden />
            Campaigns by location
          </span>
          <ProvenanceBadge provenance="live" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {legendZones.length > 0 && (
          <section aria-label="Floorplan location legend">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Floorplan legend
            </p>
            <ul className="space-y-1.5">
              {legendZones.map(({ zone, color, campaignCount }) => {
                const isSelected = selectedZoneId === zone.id;
                return (
                  <li key={zone.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelectZone?.(zone.id);
                        if (zone.instoreLocationId != null) {
                          onSelectInstoreLocation?.(zone.instoreLocationId);
                        }
                      }}
                      className={`w-full flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-sm transition-colors ${
                        isSelected ? 'border-blue-400 bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                      aria-pressed={isSelected}
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: color }}
                        aria-hidden
                      />
                      <span className="flex-1 truncate font-medium">{zone.label}</span>
                      {campaignCount > 0 && (
                        <Badge variant="secondary" className="text-[10px] shrink-0">
                          {campaignCount}
                        </Badge>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {missingBoxes.length > 0 && (
          <section aria-label="Locations not on floorplan">
            <p className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-2">
              Not on floorplan yet
            </p>
            <ul className="space-y-1.5">
              {missingBoxes.map((group) => (
                <li
                  key={group.instoreLocationId}
                  className="flex items-center gap-2 rounded-lg border border-dashed border-amber-200 bg-amber-50/50 px-2.5 py-2"
                >
                  <MapPin className="h-4 w-4 text-amber-600 shrink-0" aria-hidden />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{group.locationName}</p>
                    <p className="text-xs text-gray-500">
                      {group.campaigns.length} campaign{group.campaigns.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="shrink-0 h-8"
                    disabled={addingLocationId != null || !storeId}
                    onClick={() => void handleAddZone(group.instoreLocationId, group.locationName)}
                  >
                    {addingLocationId === group.instoreLocationId ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : (
                      <>
                        <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />
                        Add area
                      </>
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {campaignGroups.length === 0 ? (
          <p className="text-sm text-gray-500">
            No campaigns with posting locations yet. Assignments with in-store locations will appear here, grouped by aisle area.
          </p>
        ) : (
          <div className="space-y-4">
            {campaignGroups.map((group) => {
              const isSelected = selectedInstoreLocationId === group.instoreLocationId
                || (group.floorplanZoneId != null && selectedZoneId === group.floorplanZoneId);
              const evidenceImages = getInstallImagesForInstoreLocation(
                profile.images,
                group.instoreLocationId,
                group.floorplanZoneId,
                group.campaigns,
              );
              return (
                <section
                  key={group.instoreLocationId}
                  aria-label={`Campaigns at ${group.locationName}`}
                  className={`rounded-lg border ${
                    isSelected ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => selectGroup(group)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 rounded-t-lg"
                  >
                    {group.outlineColor ? (
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: group.outlineColor }}
                        aria-hidden
                      />
                    ) : (
                      <span className="h-2.5 w-2.5 rounded-full border border-dashed border-gray-400 shrink-0" aria-hidden />
                    )}
                    <span className="font-medium text-sm flex-1 truncate">{group.locationName}</span>
                    {evidenceImages.length > 0 && (
                      <Badge variant="secondary" className="text-[10px] shrink-0">
                        {evidenceImages.length} photo{evidenceImages.length === 1 ? '' : 's'}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {group.campaigns.length}
                    </Badge>
                  </button>
                  {evidenceImages.length > 0 && (
                    <div className="px-3 pb-2 flex gap-1.5 overflow-x-auto border-t pt-2">
                      {evidenceImages.map((image) => (
                        <div
                          key={image.id}
                          className="shrink-0 h-14 w-14 rounded-md overflow-hidden border border-gray-200 bg-gray-50"
                          title={image.caption}
                        >
                          <SafeImage
                            src={resolveEvidenceImageUrl(image)}
                            alt={image.caption}
                            className="h-full w-full object-cover"
                            placeholderType="photo"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <ul className="divide-y border-t">
                    {group.campaigns.map((campaign) => {
                      const campaignImages = evidenceImages.filter((image) =>
                        image.caption.toLowerCase().includes(campaign.campaignName.toLowerCase())
                        || campaign.installationImageUrls.includes(image.url)
                        || (campaign.campaignTaskId != null && image.assignmentId === campaign.campaignTaskId),
                      );
                      const thumbUrl = campaignImages[0]
                        ? resolveEvidenceImageUrl(campaignImages[0])
                        : campaign.installationImageUrls[0];
                      return (
                        <li key={campaign.id} className="px-3 py-2.5 text-sm">
                          <div className="flex items-start gap-2.5">
                            {thumbUrl ? (
                              <div className="shrink-0 h-12 w-12 rounded overflow-hidden border border-gray-200 bg-gray-50">
                                <SafeImage
                                  src={thumbUrl}
                                  alt={campaign.campaignName}
                                  className="h-full w-full object-cover"
                                  placeholderType="photo"
                                />
                              </div>
                            ) : null}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className="font-medium leading-snug">{campaign.campaignName}</p>
                                <ComplianceBadge status={campaign.complianceStatus} />
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {campaign.brand} · {campaign.phase}
                                {campaign.phase !== 'current' ? ` · ${campaign.installationDate}` : ''}
                              </p>
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {(campaign.mediaFormats?.length
                                  ? campaign.mediaFormats
                                  : [{ mediaFormatId: campaign.mediaFormatId ?? null, name: campaign.formatLabel, quantity: campaign.plannedQuantity }]
                                ).map((format) => (
                                  <span
                                    key={`${format.mediaFormatId ?? format.name}`}
                                    className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600"
                                  >
                                    {format.name} ×{format.quantity}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
